import React, { createContext, useState, useCallback, useEffect, useContext, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Tooltip, IconStatusError, IconHost, ProgressBar, Modal, useToasts } from '@seismic/mantle';
import { getService, CommonService, HttpService, ServiceAddressService, AuthService } from '@seismic/browser-services';
import * as tus from 'tus-js-client';
import { BlobStorageApiService } from '@seismic/blob-storage-api';

const UniversalUploaderContext = createContext({
    accept: '*',
    handleFiles: async () => {
        // eslint-disable-next-line no-console
        console.error('UUC: Did not set handleFiles in context');
    },
    openFileInput: () => {
        // eslint-disable-next-line no-console
        console.error('UUC: Did not set openFileInput in context');
    }
});

let draggingCount = 0;
function useFileDrop({ dragElementRef, handleFiles }) {
    const [isDragging, setIsDragging] = useState(false);
    const handleDragEnter = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        draggingCount++;
        if (event.dataTransfer?.items && event.dataTransfer.items.length !== 0) {
            setIsDragging(true);
        }
    }, []);
    const handleDragLeave = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        draggingCount--;
        if (draggingCount > 0)
            return;
        setIsDragging(false);
    }, []);
    const handleDragOver = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);
    const handleDrop = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
        draggingCount = 0;
        const eventFiles = event.dataTransfer?.files;
        if (eventFiles && eventFiles.length > 0) {
            const files = Array.from(eventFiles);
            handleFiles(files, event);
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleFiles]);
    useEffect(() => {
        const ele = dragElementRef.current;
        ele?.addEventListener('dragenter', handleDragEnter);
        ele?.addEventListener('dragleave', handleDragLeave);
        ele?.addEventListener('dragover', handleDragOver);
        ele?.addEventListener('drop', handleDrop);
        return () => {
            ele?.removeEventListener('dragenter', handleDragEnter);
            ele?.removeEventListener('dragleave', handleDragLeave);
            ele?.removeEventListener('dragover', handleDragOver);
            ele?.removeEventListener('drop', handleDrop);
        };
    }, [dragElementRef, handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);
    return isDragging;
}

function UniversalUploaderDnd(props) {
    const { handleFiles } = useContext(UniversalUploaderContext);
    const dragElementRef = useRef(null);
    const isDropping = useFileDrop({ dragElementRef, handleFiles });
    return (React.createElement("div", { className: `uuc-dnd' ${props.className} ${isDropping ? 'dragging' : ''}`, style: props.style, ref: dragElementRef }, props.children));
}

function UniversalUploaderButton(props) {
    const { openFileInput } = useContext(UniversalUploaderContext);
    const handleClick = useCallback(() => {
        openFileInput();
    }, [openFileInput]);
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const handleInput = useCallback(() => { }, []);
    return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    React.createElement("div", { onInput: handleInput, onClick: handleClick, className: `uuc-upload-button${props.className ? ' ' + props.className : ''}`, style: props.style }, props.children));
}

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z$1 = ".ProgressModalItem_uuc-upload-item-body__pQ6DO {\n  display: flex;\n  flex-direction: row;\n  height: 32px;\n  align-items: center;\n  width: 100%;\n}\n\n.ProgressModalItem_uuc-upload-item-filename__7zDLC {\n  text-overflow: ellipsis;\n  overflow: hidden;\n  white-space: nowrap;\n}\n\n.ProgressModalItem_uuc-upload-item-icon__UfniJ {\n  display: flex;\n  align-items: center;\n  padding-right: 5px;\n}\n\n.ProgressModalItem_uuc-upload-item-footer__DkS7N {\n  padding-bottom: 16px;\n}";
styleInject(css_248z$1);

function ProgressModalItem({ uucFile }) {
    const extension = getExtension(uucFile.file);
    const percent = 100 * (uucFile.bytesSent / uucFile.file.size);
    const fileName = uucFile.file.name;
    return (React.createElement("div", { className: "uuc-upload-item" },
        React.createElement("div", { className: "uuc-upload-item-body" },
            React.createElement("div", { className: "uuc-upload-item-icon" },
                uucFile.status === 'ERROR' && (React.createElement(Tooltip, { content: "Failed to upload" },
                    React.createElement(IconStatusError, { color: "red" }))),
                uucFile.status !== 'ERROR' && React.createElement(IconHost, { category: "content-icons-v2", params: { format: extension } })),
            React.createElement("div", { className: "uuc-upload-item-filename" }, fileName)),
        React.createElement("div", { className: "uuc-upload-item-footer" },
            React.createElement(ProgressBar, { variant: uucFile.status === 'ERROR' ? 'error' : 'default', valueText: "", value: percent }))));
}
const getExtension = (file) => {
    if (!file || !file.name) {
        return ''; // Will show default file icon
    }
    const fileNameSplit = file.name?.split('.');
    const extension = fileNameSplit[fileNameSplit.length - 1] || '';
    // Mantle IconHost expects 'microapp' instead of 'tiled' (they are synonymous in product)
    if (extension.toLowerCase() === 'tiled') {
        return 'microapp';
    }
    return extension;
};

function ProgressModalHeader({ numTotalFiles, numCompletedFiles }) {
    const isCompleted = useMemo(() => numCompletedFiles === numTotalFiles, [numCompletedFiles, numTotalFiles]);
    if (isCompleted) {
        return React.createElement("div", null, "Upload Complete");
    }
    return (React.createElement("div", null,
        numCompletedFiles,
        " out of ",
        numTotalFiles,
        " completed"));
}

var css_248z = ".ProgressModal_uuc-progress-modal__-8OgD .ProgressModal_uuc-modal-body__oIT3J {\n  display: flex;\n  flex-direction: column;\n}";
styleInject(css_248z);

function ProgressModal(props) {
    const completedFiles = props.uucFiles.filter((uucFile) => uucFile.status === 'COMPLETED').length;
    return (React.createElement(Modal, { onClose: props.onClose, open: props.isOpen, header: () => React.createElement(ProgressModalHeader, { numTotalFiles: props.uucFiles.length, numCompletedFiles: completedFiles }), className: "uuc-progress-modal" },
        React.createElement("div", { className: "uuc-modal-body" }, props.uucFiles.map((uucFile, index) => (React.createElement(ProgressModalItem, { key: index, uucFile: uucFile }))))));
}

// TODO if we pass in an array of empty string, then it returns false when it should be true
function shouldAccept(file, accept) {
    if (!file || !accept || accept.length === 0) {
        return true;
    }
    return matchesExtension(file, accept);
}
function shouldDeny(file, deny) {
    if (!file || !deny || deny.length === 0) {
        return false;
    }
    return matchesExtension(file, deny);
}
/**
 * Check if the provided file type should be accepted by the input with accept attribute.
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/Input#attr-accept
 *
 * Inspired by https://github.com/react-dropzone/attr-accept
 * Downside of defining out own file checking is that we will have to maintain this for future MIME types.
 *
 * @param file {File} https://developer.mozilla.org/en-US/docs/Web/API/File
 * @param accept {string} https://developer.mozilla.org/en-US/docs/Web/HTML/Element/Input#attr-accept
 * @returns {boolean} Whether the file is accepted
 */
function matchesExtension(file, accept) {
    const acceptedFilesArray = Array.isArray(accept) ? accept : accept.split(',');
    const fileName = file.name || '';
    const mimeType = (file.type || '').toLowerCase();
    const baseMimeType = mimeType.replace(/\/.*$/, '');
    return acceptedFilesArray.some((type) => {
        const validType = type.trim().toLowerCase();
        if (validType.charAt(0) === '*') {
            return true;
        }
        else if (validType.charAt(0) === '.') {
            return fileName.toLowerCase().endsWith(validType);
        }
        else if (validType.endsWith('/*')) {
            // This is something like a image/* mime type
            return baseMimeType === validType.replace(/\/.*$/, '');
        }
        return mimeType === validType;
    });
}

const DEFAULT_CHUNK_SIZE_IN_MB = 5;
function getUploader(file, uploaders) {
    for (let i = 0; i < uploaders.length; i++) {
        const uploader = uploaders[i];
        if (shouldAccept(file, uploader.accept)) {
            return uploader.uploader;
        }
    }
    // This should never happen since BssUploader is our default uploader
    throw new Error(`No uploader found for file ${file.name}`);
}
const uploadFile = async (props) => {
    const setProgress = (bytesSent, bytesTotal) => {
        props.onProgress(props.file, bytesSent, bytesTotal);
    };
    const uploaderOptions = {
        chunkSizeInMB: DEFAULT_CHUNK_SIZE_IN_MB,
        bssUploadOptions: props.bssUploadOptions,
        signal: props.signal,
        setProgress
    };
    const uploader = getUploader(props.file, props.uploaders);
    try {
        const uploadedFile = await uploader.upload(props.file, uploaderOptions);
        props.onComplete(uploadedFile);
        return uploadedFile;
    }
    catch (err) {
        // Error will bubble up and be dispatched in the consuming function
        throw new Error(`Failed to upload ${props.file.name}.`, err);
    }
};

const defaultUploaders = [];
/**
 * @param accept Allowed file types separated by commas or as an array
 *               https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/accept
 * @constructor
 */
function Uploader(accept) {
    return (constructor) => {
        const uploader = getService(constructor);
        defaultUploaders.push({ uploader: uploader, accept });
    };
}

var UucErrorCode;
(function (UucErrorCode) {
    UucErrorCode["MAX_LIMIT_EXCEEDED"] = "MAX_LIMIT_EXCEEDED";
    UucErrorCode["INVALID_FILE_EXTENSION"] = "INVALID_FILE_EXTENSION";
    UucErrorCode["FOLDER_NOT_SUPPORTED"] = "FOLDER_NOT_SUPPORTED";
})(UucErrorCode || (UucErrorCode = {}));

function hasValidLimit(files, limit, onError) {
    if (files.length > limit) {
        const error = new Error(`You can only upload ${limit} files at a time`);
        onError({ code: UucErrorCode.MAX_LIMIT_EXCEEDED, error });
        return false;
    }
    return true;
}
function hasValidExtensions(files, accept, deny, onError) {
    const invalidFiles = files.filter((file) => {
        return !shouldAccept(file, accept) || shouldDeny(file, deny);
    });
    if (invalidFiles.length > 0) {
        onError({
            code: UucErrorCode.INVALID_FILE_EXTENSION,
            error: new Error(`Failed to upload due to invalid file extensions`),
            invalidFiles
        });
        return false;
    }
    return true;
}
function hasFolder(files, dragEvent, onError) {
    // Only dragged items may have folders, user cannot select a folder from the file input
    if (dragEvent === undefined) {
        return false;
    }
    const draggedFiles = dragEvent?.dataTransfer?.files;
    const draggedItems = dragEvent?.dataTransfer?.items;
    if (!draggedFiles || !draggedItems) {
        return false; // Shouldn't get here - A user would never be able to drag nothing
    }
    const folderList = Array.from(draggedFiles).filter((file, index) => {
        const item = draggedItems[index].webkitGetAsEntry();
        return item && item.isDirectory;
    });
    if (folderList.length > 0) {
        onError({
            code: UucErrorCode.FOLDER_NOT_SUPPORTED,
            error: new Error(`We do not support uploading folders. Please upload only files.`),
            invalidFiles: folderList
        });
        return true;
    }
    return false;
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

let VfsUploader = class VfsUploader {
    constructor() {
        this.httpService = getService(HttpService);
        this.sdsService = getService(ServiceAddressService);
        this.authService = getService(AuthService);
        this.uploadVFS = this.uploadVFS.bind(this);
        this.uploadVFSProxy = this.uploadVFSProxy.bind(this);
    }
    async getCountryCode() {
        let countryCode;
        try {
            countryCode = await Promise.any([
                this.httpService.get('https://whereami.seismic.com', {
                    ignoreGlobalHeaders: true,
                    headers: { authorization: undefined }
                }),
                this.httpService.get('https://whereami.seismic-cn.com', {
                    ignoreGlobalHeaders: true,
                    headers: { authorization: undefined }
                })
            ]);
        }
        catch (ex) {
            // eslint-disable-next-line no-console
            console.warn('UUC: unable to retrieve country code', ex);
        }
        return countryCode || 'UNKNOWN';
    }
    async upload(file, options) {
        const countryCode = await this.getCountryCode();
        const uploadFunction = ['CN'].indexOf(countryCode || 'UNKNOWN') >= 0 ? this.uploadVFSProxy : this.uploadVFS;
        return uploadFunction(file, options);
    }
    uploadVFS(file, options) {
        return new Promise(async (resolve, reject) => {
            try {
                const VFS_API_URL = await this.sdsService.getServiceAddressByKey('vfs');
                const response = await this.httpService.post(`${VFS_API_URL}/api/v1/videos/upload/bootstrap`, { size: file.size });
                const upload = new tus.Upload(file, {
                    endpoint: response.url,
                    uploadUrl: response.url,
                    retryDelays: [0, 1000, 3000, 5000],
                    onError: (err) => {
                        reject(err);
                    },
                    onProgress: (bytesSent, bytesTotal) => {
                        if (options.signal?.aborted) {
                            upload.abort();
                        }
                        else {
                            options.setProgress(bytesSent, bytesTotal);
                        }
                    },
                    onSuccess: () => {
                        resolve({
                            storageService: 'vfs',
                            storageId: response.storageId,
                            file
                        });
                    }
                });
                upload.start();
            }
            catch (ex) {
                reject(ex);
            }
        });
    }
    async uploadVFSProxy(file, options) {
        // eslint-disable-next-line no-console
        console.warn('UUC: VFS is not available in your region, falling back to VFS Proxy');
        return new Promise(async (resolve, reject) => {
            try {
                const authToken = await this.authService.getAccessToken();
                const uvrgUrl = await this.sdsService.getServiceAddressByKey('uvrg');
                const upload = new tus.Upload(file, {
                    headers: {
                        Authorization: `${authToken}`
                    },
                    endpoint: `${uvrgUrl}/api/v1/upload`,
                    chunkSize: 15000000,
                    retryDelays: [0, 3000, 5000, 10000],
                    metadata: {
                        filename: file.name ?? 'default',
                        filetype: file.type
                    },
                    onError: (err) => {
                        // eslint-disable-next-line no-console
                        console.error('UUC: VFS Proxy Error:', err);
                        reject(err);
                    },
                    onProgress: (bytesSent, bytesTotal) => {
                        if (options.signal?.aborted) {
                            upload.abort();
                        }
                        else {
                            options.setProgress(bytesSent, bytesTotal);
                        }
                    },
                    onSuccess: async () => {
                        const id = upload.url?.substring(upload.url.lastIndexOf('/') + 1);
                        let response;
                        try {
                            response = await this.httpService.post(`${uvrgUrl}/api/v3/process/start/${id}0`);
                        }
                        catch (err) {
                            // eslint-disable-next-line no-console
                            console.error('UUC: VFS Proxy Error:', err);
                            return reject(err);
                        }
                        resolve({
                            storageService: 'vfs',
                            storageId: response?.storageId,
                            file
                        });
                    }
                });
                upload.start();
            }
            catch (ex) {
                reject(ex);
            }
        });
    }
};
VfsUploader = __decorate([
    Uploader('video/*'),
    CommonService('Uuc_VfsUploader'),
    __metadata("design:paramtypes", [])
], VfsUploader);

const APPLICATION_NAME = 'uuc';
const APPLICATION_VERSION = '0.2'; // TODO (later) is there a way to automate this? (based on major.minor FE version)
let BssUploader = class BssUploader {
    async upload(file, options) {
        const bssService = getService(BlobStorageApiService);
        // Wrapper: BSS implements progressCallback with a percentage, but UUC prefers bytes (inspired by TUS)
        const setBssProgress = (percent) => {
            options.setProgress(file.size * percent, file.size);
        };
        const bssUploadOptions = {
            chunkSizeInMB: 5,
            applicationName: APPLICATION_NAME,
            applicationVersion: APPLICATION_VERSION,
            ...options.bssUploadOptions,
            progressCallback: setBssProgress
        };
        const result = await bssService.uploadFile(file, bssUploadOptions);
        return {
            storageService: 'bss',
            storageId: result.id,
            containerId: options.bssUploadOptions.container,
            file
        };
    }
};
BssUploader = __decorate([
    Uploader('*'),
    CommonService('Uuc_BssUploader')
], BssUploader);

const UniversalUploader = forwardRef((props, ref) => {
    const { addToast } = useToasts();
    const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);
    const [files, setFiles] = useState([]);
    const [uucFilesByName, setUucFilesByName] = useState({});
    const [shouldUpload, setShouldUpload] = useState(false);
    const limit = props.limit; // Defaults to 1
    const uploaders = [...(props.customUploaders || []), ...defaultUploaders];
    const divRef = useRef(null);
    const inputRef = useRef(null);
    const inputAccept = Array.isArray(props.accept) ? props.accept.join(',') : props.accept;
    useImperativeHandle(ref, () => {
        return {
            continueUpload: (modifiedFiles) => {
                if (modifiedFiles && modifiedFiles.length > 0) {
                    const newFiles = Array.from(modifiedFiles);
                    setFiles(newFiles);
                    startUpload(newFiles);
                }
                else {
                    startUpload(files);
                }
            },
            cancelUpload: () => {
                cleanupUpload();
            }
        };
    });
    // Prop validation
    if (props.accept === undefined || !props.accept.length) {
        throw new Error('UUC: accept must be specified');
    }
    if (limit < -1) {
        throw new Error(`UUC: limit (${limit}) must be equal to or greater than -1`);
    }
    const onError = (uucError) => {
        props.onError?.(uucError);
        // eslint-disable-next-line no-console
        console.error(`UUC: ${uucError.error.message}`, uucError);
        if (props.showToastOnError) {
            addToast({
                variant: 'error',
                content: uucError.error.message,
                autoDismiss: false
            });
        }
    };
    /**
     * Handle the files selected by the user.
     * @param selectedFiles
     */
    const handleFiles = async (selectedFiles, dragEvent) => {
        if (!hasValidLimit(selectedFiles, limit, onError) ||
            !hasValidExtensions(selectedFiles, props.accept, props.deny, onError) ||
            hasFolder(selectedFiles, dragEvent, onError)) {
            return;
        }
        setFiles(selectedFiles); // Need to store files in case of disjoint flow and files are not passed back
        props.onFilesSelected?.(Array.from(selectedFiles));
        // if props.validate exits, perform custom validation
        const isValid = props.validate ? await props.validate(Array.from(selectedFiles)) : true;
        setShouldUpload(isValid);
        if (!isValid) {
            // if custom validation returns false, pause upload (disjoint flow)
            return;
        }
        startUpload(selectedFiles);
    };
    const startUpload = (selectedFiles) => {
        const newUucFilesByName = {};
        selectedFiles.map((file) => {
            const uucFile = {
                file,
                bytesSent: 0,
                status: 'PENDING'
            };
            newUucFilesByName[file.name] = uucFile;
        });
        setUucFilesByName(newUucFilesByName);
        if (props.showProgressModal) {
            setIsProgressModalOpen(true);
        }
        setShouldUpload(true);
    };
    const handleOpenFileInput = () => inputRef.current?.click();
    const handleInputFileChange = (event) => {
        const files = Array.from(event.target.files);
        handleFiles(files);
        event.target.value = ''; // Reset the file input value, so we can upload the same file(s) again
    };
    const handleProgressModalClose = () => {
        //TODO confirm close if not complete here?
        const uucFiles = getUucFiles();
        const uploadedFiles = uucFiles
            .filter((uucFile) => uucFile.status === 'COMPLETED' && uucFile.uploadedFile)
            .map((uucFile) => uucFile.uploadedFile);
        const notUploadedFiles = uucFiles.filter((file) => file.status !== 'COMPLETED');
        props.onProgressModalClose?.(uploadedFiles, notUploadedFiles.map((uucFile) => uucFile.file)
        // erroredFiles // TODO (feature request) if someone needs it (non breaking change)
        );
        setIsProgressModalOpen(false);
        cleanupUpload();
    };
    const onFileProgress = (file, bytesSent, bytesTotal) => {
        setUucFilesByName((currentFiles) => {
            const fileToUpdate = currentFiles[file.name];
            const updatedFile = { ...fileToUpdate, bytesSent, status: 'UPLOADING' };
            return { ...currentFiles, [file.name]: updatedFile };
        });
        props.onFileProgress?.(file, bytesSent, bytesTotal);
    };
    const onFileComplete = (uploadedFile) => {
        setUucFilesByName((currentFiles) => {
            const fileToUpdate = currentFiles[uploadedFile.file.name];
            const updatedFile = { ...fileToUpdate, uploadedFile, status: 'COMPLETED' };
            return { ...currentFiles, [uploadedFile.file.name]: updatedFile };
        });
        props.onFileComplete?.(uploadedFile);
    };
    const onFileError = (file, error) => {
        setUucFilesByName((currentFiles) => {
            const fileToUpdate = currentFiles[file.name];
            const updatedFile = { ...fileToUpdate, status: 'ERROR' };
            return { ...currentFiles, [file.name]: updatedFile };
        });
        if (props.showToastOnError) {
            addToast({
                variant: 'error',
                content: error.message,
                autoDismiss: false
            });
        }
        props.onFileError?.(file, error);
    };
    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;
        if (!files || files.length === 0 || !shouldUpload) {
            return;
        }
        const uploadFiles = async () => {
            let hasErrorOnUpload = false;
            const uploadedFiles = [];
            // Upload Files in order (not in parallel)
            // Parallel Uploads: https://stackoverflow.com/questions/37576685/using-async-await-with-a-foreach-loop
            // In Order - use for (const file of files)
            // In Parallel - use Promise.all(files.map(async (file) => { ... }))
            for (const file of files) {
                const uploadFileProps = {
                    file,
                    signal,
                    uploaders,
                    bssUploadOptions: props.bssUploadOptions,
                    onProgress: onFileProgress,
                    onComplete: onFileComplete
                };
                let uploadedFile;
                try {
                    uploadedFile = await uploadFile(uploadFileProps);
                }
                catch (error) {
                    hasErrorOnUpload = true;
                    // eslint-disable-next-line no-console
                    console.error(`UUC: ${error.message}`, error);
                    onFileError(file, error);
                    // Prevent to avoid adding failed file to uploadedFiles
                    continue;
                }
                uploadedFiles.push(uploadedFile);
                if (signal.aborted) {
                    break;
                }
            }
            // All files have been uploaded (regardless of errors), finish up and close if no errors
            props.onComplete(uploadedFiles);
            if (!hasErrorOnUpload) {
                // TODO need some sort of delay for BSS - currently closes before progress shows complete
                setIsProgressModalOpen(false);
                cleanupUpload();
            }
            return uploadedFiles;
        };
        uploadFiles();
        // Setting additional dependencies may cause refresh issues (wiping out current upload)
        // TODO (later) properly handle additional files being added during upload
        return () => {
            controller.abort();
        };
        // TODO (later) can we add all exhaustive deps?
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files, shouldUpload]);
    const cleanupUpload = () => {
        setFiles([]);
        setUucFilesByName({});
        setShouldUpload(false);
    };
    const getUucFiles = () => {
        return Object.values(uucFilesByName);
    };
    return (React.createElement(UniversalUploaderContext.Provider, { value: {
            accept: props.accept,
            handleFiles,
            openFileInput: handleOpenFileInput
        } },
        React.createElement("div", { ref: divRef, className: props.className, style: props.style },
            React.createElement("input", { onInput: handleInputFileChange, multiple: props.limit > 1, hidden: true, ref: inputRef, type: "file", accept: inputAccept, style: { display: 'none' } }),
            props.showProgressModal && (React.createElement(ProgressModal, { uucFiles: getUucFiles(), isOpen: isProgressModalOpen, onClose: handleProgressModalClose })),
            props.children)));
});
UniversalUploader.defaultProps = {
    limit: 1,
    bssUploadOptions: {
        isFullContainerName: true
    },
    showProgressModal: true,
    showToastOnError: true
};
// TODO @ts-ignore on these because of this error:
// Property 'DnD' does not exist on type 'ForwardRefExoticComponent<UniversalUploaderComponentProps & RefAttributes<UniversalUploaderRef>>'
/**
 * @deprecated
 */
// @ts-ignore
UniversalUploader.DnD = UniversalUploaderDnd;
/**
 * @deprecated
 */
// @ts-ignore
UniversalUploader.Button = UniversalUploaderButton;

export { UniversalUploader, UniversalUploaderButton, UniversalUploaderDnd };
//# sourceMappingURL=index.js.map
